import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, CopyableId, Input } from '@fiado/react-kit';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { handleApiError } from '../../api/errorMapping';
import { ApiError } from '../../api/errors';
import { generateIdempotencyKey } from '../../lib/idempotency';
import { slugifyRole, isValidRoleSlug } from '../../lib/slugify';
import { PermissionPicker } from '../permissions/PermissionPicker';
import { TechnicalDetails } from '../shared/TechnicalDetails';
import type { Permission, Scope } from '../../api/types/permissions';

// El usuario escribe un nombre legible (displayName); el roleId (slug snake_case) se autocalcula del
// nombre. Validamos que el nombre produzca un slug válido (el backend re-valida el mismo regex).
// Al editar, el roleId es fijo de por vida: solo se renombra el label visible.
const schema = z.object({
    displayName: z
        .string()
        .trim()
        .min(1, 'Escribe un nombre para el rol.')
        .refine(
            isValidRoleSlug,
            'El nombre debe generar un ID válido: al menos 3 letras o números, empezando con una letra.',
        ),
    description: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

/** Body de creación (roleName autocalculado del displayName). */
export interface RoleCreateBody {
    roleName: string;
    displayName: string;
    description?: string;
    permissions: Permission[];
}

/** Body de edición (el roleId es fijo; solo se renombra el label + permisos). */
export interface RoleUpdateBody {
    displayName: string;
    description?: string;
    permissions: Permission[];
}

/**
 * Estrategia inyectada por cada call-site. Concentra TODO lo que diverge entre los 3 flujos de rol
 * (tenant propio, PLATFORM, drill-down platform-tenant): binding de API + invalidación, variante del
 * `PermissionPicker`, si soporta edición, valores iniciales y los textos/ids que difieren.
 */
export interface RoleFormStrategy {
    /** ¿El flujo soporta edición? (`false` ⇒ solo alta, sin `update`). */
    supportsEdit: boolean;
    /** Rol en edición (roleId fijo → CopyableId + `update`). Ausente ⇒ modo crear. */
    role?: { roleId: string };
    /** Valores iniciales del form (el wrapper los deriva de su tipo de rol / props de clonado). */
    initial: { displayName: string; description: string; permissions: Permission[] };
    /** Crea el rol. */
    create: (body: RoleCreateBody, opts: { idempotencyKey: string }) => Promise<unknown>;
    /** Actualiza el rol (requerido si `supportsEdit` y hay `role`). */
    update?: (body: RoleUpdateBody) => Promise<unknown>;
    /** Invalida las queries afectadas tras crear/editar. */
    invalidate: () => Promise<void>;
    /** Props del PermissionPicker: `scopeFilter` (PLATFORM), `tenantId` (tenant visto) o ninguno. */
    permissionPickerProps?: { scopeFilter?: Scope; tenantId?: string };
    /** Se llama tras crear/editar OK (ej. cerrar el modal). */
    onDone?: () => void;
    /** Fallback tras CREAR si no hay `onDone` (ej. navegar a la lista). */
    onCreatedFallback?: () => void | Promise<void>;
    /** Prefijo de los `id` de los campos (evita colisiones si conviven en el DOM). */
    idPrefix: string;
    /** Texto de ayuda del campo nombre. */
    nameHint: string;
    /** Etiqueta del bloque de permisos ("Permisos" / "Permissions"). */
    permissionsLabel: string;
    /** Texto del submit al CREAR (en edición siempre "Guardar"). */
    createSubmitLabel: string;
}

/**
 * Core compartido del formulario de rol. Unifica el schema, el preview del slug, el `PermissionPicker`,
 * el manejo de colisión de slug (`ROLE_ALREADY_EXISTS`) y las afordancias de edición (CopyableId del
 * roleId inmutable). Las 3 pantallas de rol son wrappers delgados que solo arman su `strategy`.
 */
export function RoleFormCore({ strategy }: { strategy: RoleFormStrategy }) {
    const isEdit = !!strategy.role;
    const [permissions, setPermissions] = useState<Permission[]>(strategy.initial.permissions);
    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            displayName: strategy.initial.displayName,
            description: strategy.initial.description,
        },
    });
    // Preview en vivo del ID que se va a generar (solo al crear; al editar el roleId es fijo).
    const displayName = useWatch({ control: form.control, name: 'displayName' });
    const slugPreview = slugifyRole(displayName);

    const mut = useMutation({
        mutationFn: (v: FormValues) =>
            isEdit && strategy.update
                ? strategy.update({
                      displayName: v.displayName.trim(), // el label visible — texto libre
                      description: v.description,
                      permissions,
                  })
                : strategy.create(
                      {
                          roleName: slugifyRole(v.displayName), // el ID (llave) — autocalculado
                          displayName: v.displayName.trim(), // el label visible — texto libre
                          description: v.description,
                          permissions,
                      },
                      { idempotencyKey: generateIdempotencyKey() },
                  ),
        onSuccess: async () => {
            toast.success(isEdit ? 'Rol actualizado' : 'Rol creado');
            await strategy.invalidate();
            if (strategy.onDone) strategy.onDone();
            else if (!isEdit) await strategy.onCreatedFallback?.();
        },
        onError: (err) => {
            // Colisión de slug al crear: el backend tira 409 ROLE_ALREADY_EXISTS. Lo mostramos en el
            // campo del nombre (no auto-sufijo: que el usuario elija otro nombre).
            if (err instanceof ApiError && err.code === 'ROLE_ALREADY_EXISTS') {
                form.setError('displayName', {
                    message: 'Ya existe un rol con ese nombre. Elige otro nombre.',
                });
                return;
            }
            handleApiError(err);
        },
    });

    return (
        <form
            onSubmit={(e) => {
                void form.handleSubmit((v) => {
                    mut.mutate(v);
                })(e);
            }}
            className="space-y-4"
        >
            <div className="space-y-1">
                <Input
                    id={`${strategy.idPrefix}-name`}
                    label="Nombre del rol"
                    hint={strategy.nameHint}
                    error={form.formState.errors.displayName?.message}
                    {...form.register('displayName')}
                />
            </div>
            {(isEdit && strategy.role) || slugPreview ? (
                <TechnicalDetails>
                    {isEdit && strategy.role ? (
                        <CopyableId label="Identificador del rol" value={strategy.role.roleId} />
                    ) : (
                        <CopyableId label="Identificador que se generará" value={slugPreview} />
                    )}
                </TechnicalDetails>
            ) : null}
            <div className="space-y-2">
                <Label htmlFor={`${strategy.idPrefix}-desc`}>Descripción</Label>
                <Textarea id={`${strategy.idPrefix}-desc`} {...form.register('description')} />
            </div>
            <div className="space-y-2">
                <Label>{strategy.permissionsLabel}</Label>
                <PermissionPicker
                    value={permissions}
                    onChange={setPermissions}
                    scopeFilter={strategy.permissionPickerProps?.scopeFilter}
                    tenantId={strategy.permissionPickerProps?.tenantId}
                />
            </div>
            <Button type="submit" disabled={mut.isPending || permissions.length === 0}>
                {isEdit ? 'Guardar' : strategy.createSubmitLabel}
            </Button>
        </form>
    );
}
